import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('nfx_token'));
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor for auth
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const storedToken = localStorage.getItem('nfx_token');
      if (storedToken) {
        config.headers.Authorization = `Bearer ${storedToken}`;
      }
      return config;
    });

    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('nfx_token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem('nfx_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    // Clear any existing token before login to avoid conflicts
    localStorage.removeItem('nfx_token');
    setToken(null);
    setUser(null);
    
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('nfx_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const loginWithPasskey = async (email) => {
    // Step 1: Get authentication options
    const optionsResp = await axios.post(`${API}/auth/webauthn/login-options`, { email });
    const options = optionsResp.data;
    
    // Step 2: Convert base64url to ArrayBuffer
    const toBuffer = (b64) => {
      const str = b64.replace(/-/g, '+').replace(/_/g, '/');
      const pad = str.length % 4 === 0 ? '' : '='.repeat(4 - (str.length % 4));
      const bin = atob(str + pad);
      return Uint8Array.from(bin, c => c.charCodeAt(0)).buffer;
    };
    
    const publicKeyOptions = {
      challenge: toBuffer(options.challenge),
      timeout: options.timeout,
      rpId: options.rpId,
      allowCredentials: options.allowCredentials.map(c => ({
        type: c.type,
        id: toBuffer(c.id),
      })),
      userVerification: options.userVerification,
    };
    
    // Step 3: Browser prompts Face ID / fingerprint
    const assertion = await navigator.credentials.get({ publicKey: publicKeyOptions });
    
    // Step 4: Convert response to base64url
    const toBase64url = (buffer) => {
      const bytes = new Uint8Array(buffer);
      let str = '';
      bytes.forEach(b => str += String.fromCharCode(b));
      return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    };
    
    const body = {
      id: toBase64url(assertion.rawId),
      rawId: toBase64url(assertion.rawId),
      type: assertion.type,
      user_id: options.user_id,
      response: {
        authenticatorData: toBase64url(assertion.response.authenticatorData),
        clientDataJSON: toBase64url(assertion.response.clientDataJSON),
        signature: toBase64url(assertion.response.signature),
        userHandle: assertion.response.userHandle ? toBase64url(assertion.response.userHandle) : null,
      },
    };
    
    // Step 5: Verify on server
    const verifyResp = await axios.post(`${API}/auth/webauthn/login-complete`, body);
    const { access_token, user: userData } = verifyResp.data;
    
    localStorage.setItem('nfx_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, fullName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name: fullName
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('nfx_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('nfx_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginWithPasskey, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
